import React from 'react';
import PropTypes from 'prop-types';
import BreadcrumbLayout from '../../../shared/components/common/breadcrumbLayout';
import ImageModal from '../../../shared/components/common/imageModal';
import ClientHelpModal from '../../clientHelpModal/clientHelpModal';
import { FeedbackState, StatusPill } from '../../../shared/ui';
import { formatarData } from '../../../shared/utils/dateFormatters';
import EquipmentHistorySection from '../components/EquipmentHistorySection';
import EquipmentInfoSection from '../components/EquipmentInfoSection';
import EquipmentPhotosSection from '../components/EquipmentPhotosSection';
import {
    ContentArea,
    EmptyState,
    HeaderArea,
    HeaderStatusBox,
    HeaderStatusRow,
    MainContent,
    PageContainer,
    PageShell,
} from '../styles';

const parseDate = (value) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return parsedDate;
};

const isPendingReview = (nextReview) => {
    const parsedDate = parseDate(nextReview);
    if (!parsedDate) return true;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return parsedDate < now;
};

function ClientEquipamentDetailsView({
    title,
    breadcrumbItems,
    isLoading,
    error,
    currentLanguage,
    loadingMessage,
    emptyMessage,
    equipment,
    photos,
    reports,
    selectedImage,
    isImageModalOpen,
    onPhotoClick,
    onCloseImageModal,
    onDownloadReport,
    downloadError,
}) {
    const sharedLanguage = currentLanguage.bepiService.shared;
    const clientLanguage = currentLanguage.bepiService.client;

    const pendingReview = isPendingReview(equipment?.proxima_revisao);
    const reviewValue = pendingReview
        ? sharedLanguage.shared_equip_card_pending_status
        : formatarData(equipment?.proxima_revisao);
    const reviewVariant = pendingReview ? 'danger' : 'info';

    const infoLabels = {
        dataSectionTitle: clientLanguage.client_equip_details_item_data_title,
        codeLabel: sharedLanguage.shared_equip_card_code_label,
        itemLabel: sharedLanguage.shared_equip_item_label,
        brandLabel: sharedLanguage.shared_equip_brand_label,
        modelLabel: sharedLanguage.shared_equip_model_label,
        caLabel: sharedLanguage.shared_equip_ca_label,
        serialLabel: sharedLanguage.shared_equip_serial_label,
        lastRevisionLabel: sharedLanguage.shared_equip_last_rev_label,
        nextRevisionLabel: sharedLanguage.shared_equip_next_rev_label,
        qrCodeLabel: sharedLanguage.shared_equip_qr_code_label,
    };

    return (
        <BreadcrumbLayout
            title={title}
            breadcrumbItems={breadcrumbItems}
            currentLanguage={currentLanguage}
            showBackButton
        >
            <ClientHelpModal view='VIEW_CLIENT_EQUIPMENT_DETAILS' currentLanguage={currentLanguage} />

            <MainContent>
                <PageContainer>
                    <PageShell>
                        {!isLoading && !error && equipment ? (
                            <HeaderArea>
                                <HeaderStatusRow>
                                    <span className='title'>{equipment.nome || '--'}</span>
                                    <HeaderStatusBox>
                                        <span className='label'>{sharedLanguage.shared_equip_next_rev_label}</span>
                                        <StatusPill variant={reviewVariant} context='onDark'>
                                            {reviewValue}
                                        </StatusPill>
                                    </HeaderStatusBox>
                                </HeaderStatusRow>
                            </HeaderArea>
                        ) : null}

                        <ContentArea>
                            {isLoading || error ? (
                                <FeedbackState
                                    loading={isLoading}
                                    error={error}
                                    loadingMessage={loadingMessage}
                                />
                            ) : null}

                            {!isLoading && !error && !equipment ? (
                                <EmptyState>{emptyMessage}</EmptyState>
                            ) : null}

                            {!isLoading && !error && equipment ? (
                                <>
                                    <EquipmentInfoSection equipment={equipment} labels={infoLabels} />

                                    <EquipmentPhotosSection
                                        sectionTitle={clientLanguage.client_equip_details_latest_photos_title}
                                        photos={photos}
                                        onPhotoClick={onPhotoClick}
                                        emptyMessage={sharedLanguage.shared_equip_no_photos_msg}
                                        fallbackAltText={sharedLanguage.shared_equip_photo_alt}
                                    />

                                    <EquipmentHistorySection
                                        sectionTitle={clientLanguage.client_equip_details_revision_history_title}
                                        reports={reports}
                                        downloadLabel={sharedLanguage.shared_equip_download_label}
                                        emptyMessage={sharedLanguage.shared_equip_no_history_msg}
                                        onDownloadReport={onDownloadReport}
                                        downloadError={downloadError}
                                    />
                                </>
                            ) : null}
                        </ContentArea>
                    </PageShell>
                </PageContainer>

                <ImageModal
                    isOpen={isImageModalOpen}
                    imageUrl={selectedImage}
                    onClose={onCloseImageModal}
                    currentLanguage={currentLanguage}
                />
            </MainContent>
        </BreadcrumbLayout>
    );
}

ClientEquipamentDetailsView.propTypes = {
    title: PropTypes.string.isRequired,
    breadcrumbItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    currentLanguage: PropTypes.object.isRequired,
    loadingMessage: PropTypes.string.isRequired,
    emptyMessage: PropTypes.string.isRequired,
    equipment: PropTypes.object,
    photos: PropTypes.arrayOf(PropTypes.object),
    reports: PropTypes.arrayOf(PropTypes.object),
    selectedImage: PropTypes.string,
    isImageModalOpen: PropTypes.bool,
    onPhotoClick: PropTypes.func.isRequired,
    onCloseImageModal: PropTypes.func.isRequired,
    onDownloadReport: PropTypes.func.isRequired,
    downloadError: PropTypes.string,
};

ClientEquipamentDetailsView.defaultProps = {
    isLoading: false,
    error: '',
    equipment: null,
    photos: [],
    reports: [],
    selectedImage: null,
    isImageModalOpen: false,
    downloadError: '',
};

export default ClientEquipamentDetailsView;
